/**
 * R-11 — evaluate daily spend thresholds and dispatch cost alarms.
 * M3 alerting via `src/lib/ops/alertingDispatcher.ts` for KPI alerts;
 * cost alarms still log via logAuditEvent until unified in a follow-up.
 */
import { logAuditEvent } from '../telemetry/auditTelemetry';
import type { DailyUserCostAggregate } from './dailyAggregate';

export interface CostAlarmThresholds {
  /** Warn when a single UID exceeds this daily USD spend. */
  warnPerUserUsd: number;
  /** Block (when hard-block enabled) when a UID exceeds this daily USD spend. */
  blockPerUserUsd: number;
  /** Warn when a customer (company) exceeds this daily USD spend. */
  warnPerCustomerUsd: number;
}

export const COST_ALARM_CONFIG: CostAlarmThresholds = {
  warnPerUserUsd: 25,
  blockPerUserUsd: 50,
  warnPerCustomerUsd: 200,
};

export type CostAlarmKind = 'user-warn' | 'user-block' | 'customer-warn';

export interface CostAlarmEvaluation {
  kind: CostAlarmKind;
  uid?: string;
  companyId?: string;
  date: string;
  totalCostUsd: number;
  thresholdUsd: number;
  message: string;
}

export interface CustomerDailyRollup {
  companyId: string;
  totalCostUsd: number;
}

export function evaluateAlarms(
  date: string,
  userAggregates: DailyUserCostAggregate[],
  customerRollups: CustomerDailyRollup[],
  thresholds: CostAlarmThresholds = COST_ALARM_CONFIG,
): CostAlarmEvaluation[] {
  const out: CostAlarmEvaluation[] = [];

  for (const row of userAggregates) {
    if (row.totalCostUsd >= thresholds.blockPerUserUsd) {
      out.push({
        kind: 'user-block',
        uid: row.uid,
        companyId: row.companyId ?? undefined,
        date,
        totalCostUsd: row.totalCostUsd,
        thresholdUsd: thresholds.blockPerUserUsd,
        message: `UID ${row.uid} daily Gemini spend $${row.totalCostUsd.toFixed(2)} exceeds block threshold $${thresholds.blockPerUserUsd}`,
      });
    } else if (row.totalCostUsd >= thresholds.warnPerUserUsd) {
      out.push({
        kind: 'user-warn',
        uid: row.uid,
        companyId: row.companyId ?? undefined,
        date,
        totalCostUsd: row.totalCostUsd,
        thresholdUsd: thresholds.warnPerUserUsd,
        message: `UID ${row.uid} daily Gemini spend $${row.totalCostUsd.toFixed(2)} exceeds warn threshold $${thresholds.warnPerUserUsd}`,
      });
    }
  }

  for (const customer of customerRollups) {
    if (customer.totalCostUsd >= thresholds.warnPerCustomerUsd) {
      out.push({
        kind: 'customer-warn',
        companyId: customer.companyId,
        date,
        totalCostUsd: customer.totalCostUsd,
        thresholdUsd: thresholds.warnPerCustomerUsd,
        message: `Customer ${customer.companyId} daily Gemini spend $${customer.totalCostUsd.toFixed(2)} exceeds warn threshold $${thresholds.warnPerCustomerUsd}`,
      });
    }
  }

  return out;
}

/** Persist alarm history + emit audit telemetry. Never throws. */
export async function dispatchCostAlarms(
  evaluations: CostAlarmEvaluation[],
): Promise<void> {
  if (evaluations.length === 0) return;

  for (const alarm of evaluations) {
    try {
      const eventType = alarm.kind === 'user-block'
        ? 'cost-block-fired'
        : 'cost-warn-fired';

      logAuditEvent({
        type: eventType,
        payload: {
          uid: alarm.uid,
          kind: alarm.kind,
          date: alarm.date,
          companyId: alarm.companyId,
          totalCostUsd: alarm.totalCostUsd,
          thresholdUsd: alarm.thresholdUsd,
          message: alarm.message,
        },
      });

      try {
        const { auth, db } = await import('../../firebase');
        if (!auth.currentUser) continue;
        const { collection, addDoc } = await import('firebase/firestore');
        await addDoc(collection(db, 'billing', 'alarmHistory'), {
          ...alarm,
          firedAt: new Date().toISOString(),
          acknowledged: false,
        });
      } catch (err) {
        console.warn('[costAlarmDispatcher] alarmHistory write failed:', (err as Error)?.message);
      }
    } catch (err) {
      console.warn('[costAlarmDispatcher] dispatch failed:', (err as Error)?.message);
    }
  }
}
